import type { ControlInterval } from '@rcs/physics';

/** ブーストの加速度 [m/s²]（約 1.5 G）。 */
export const BOOST_ACCELERATION = 15;

/** ブーストを続ける時間 [s]。 */
export const BOOST_DURATION = 2;

/** 押している間の加速の強さ（スライダー）の範囲 [m/s²]。 */
export const THROTTLE_RANGE = { min: 0.5, max: 20, step: 0.5, initial: 3 } as const;

/**
 * 走行中の操作（ボタンで加速・ブースト）の記録。時刻はシミュレーションの時刻（再生位置）[s]。
 * 物理の `control`（{@link ControlInterval} の列）に変換して、走行を計算し直す（ADR-0013）。
 * 時刻は前へしか進まない前提（最初から再生し直すときは {@link clear} する）。DOM・React に依存しない。
 */
export class ControlTimeline {
  /** 押して離した操作 */
  private readonly closed: ControlInterval[] = [];
  /** ブースト */
  private readonly boosts: ControlInterval[] = [];
  private holding: { readonly start: number; readonly acceleration: number } | undefined;

  /** 押している（加速中）か。 */
  get isHolding(): boolean {
    return this.holding !== undefined;
  }

  /** 操作が 1 つもないか。 */
  get isEmpty(): boolean {
    return this.closed.length === 0 && this.boosts.length === 0 && this.holding === undefined;
  }

  /** 最初の操作の時刻（操作がなければ `undefined`）。 */
  get firstStart(): number | undefined {
    const starts = [...this.closed, ...this.boosts].map((interval) => interval.start);
    if (this.holding) starts.push(this.holding.start);
    return starts.length > 0 ? Math.min(...starts) : undefined;
  }

  /** 物理に渡す入力。押している分は、終わりなし（`Infinity`）。 */
  get intervals(): readonly ControlInterval[] {
    return this.holding
      ? [
          ...this.closed,
          ...this.boosts,
          { start: this.holding.start, end: Infinity, acceleration: this.holding.acceleration },
        ]
      : [...this.closed, ...this.boosts];
  }

  /** 時刻 `time` から、加速度 `acceleration` で押し始める（押している間は何もしない）。変わったら `true`。 */
  press(time: number, acceleration: number): boolean {
    if (this.holding) return false;
    this.holding = { start: time, acceleration };
    return true;
  }

  /** 時刻 `time` で離す。押していなければ何もしない。変わったら `true`。 */
  release(time: number): boolean {
    if (!this.holding) return false;
    this.close(time);
    return true;
  }

  /** 押している間に、加速の強さを変える（時刻 `time` で区切る）。変わったら `true`。 */
  setAcceleration(time: number, acceleration: number): boolean {
    if (!this.holding || this.holding.acceleration === acceleration) return false;
    this.close(time);
    this.holding = { start: time, acceleration };
    return true;
  }

  /** 時刻 `time` から {@link BOOST_DURATION} 秒、{@link BOOST_ACCELERATION} で急加速する。 */
  boost(time: number): void {
    this.boosts.push({ start: time, end: time + BOOST_DURATION, acceleration: BOOST_ACCELERATION });
  }

  /** ブースト中か（時刻 `time` で）。 */
  isBoosting(time: number): boolean {
    return this.boosts.some((interval) => interval.start <= time && time < interval.end);
  }

  /** すべての操作を消す。 */
  clear(): void {
    this.closed.length = 0;
    this.boosts.length = 0;
    this.holding = undefined;
  }

  private close(time: number): void {
    const holding = this.holding;
    this.holding = undefined;
    // 押してすぐ離した（時間の幅がない）操作は残さない
    if (holding && time > holding.start) {
      this.closed.push({ start: holding.start, end: time, acceleration: holding.acceleration });
    }
  }
}
